import React, { useState, useEffect } from 'react';
import { get } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG', {minimumFractionDigits:2});

const EVENT_COLORS = {
  purchase_order_receive:{ bg:'#E8F5EB', color:'#2E6B3A', label:'PO Receive', icon:'📦' },
  manual_adjust:{ bg:'#EDF3FF', color:'#3B5BDB', label:'Manual Adjust', icon:'✏️' },
  job_material_issue:{ bg:'#FDEAEA', color:'#8F2E24', label:'Job Issue', icon:'🔧' },
  job_material_return:{ bg:'#FFF3DC', color:'#8B5E0A', label:'Job Return', icon:'↩️' },
  sale:{ bg:'#F3EEFF', color:'#6B2FA0', label:'Sale', icon:'💰' },
  stock_take:{ bg:'#F0F0FF', color:'#5C5FDB', label:'Stock Take', icon:'📋' },
  damage:{ bg:'#FDEAEA', color:'#8F2E24', label:'Damage', icon:'⚠️' },
  opening_stock:{ bg:'#F2EFE7', color:'#556B2F', label:'Opening Stock', icon:'🏁' },
};

export default function StockLedger() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('ledger');
  const [filters, setFilters] = useState({
    item_type:'', event_type:'', from:'', to:'', search:''
  });

  useEffect(() => { loadLedger(); loadSummary(); }, []);

  const loadLedger = (f=filters) => {
    setLoading(true);
    const p = new URLSearchParams();
    if (f.item_type) p.append('item_type', f.item_type);
    if (f.event_type) p.append('event_type', f.event_type);
    if (f.from) p.append('from', f.from);
    if (f.to) p.append('to', f.to);
    if (f.search) p.append('search', f.search);
    get('/inventory-events?'+p.toString())
      .then(d=>setEvents(d.data||[]))
      .finally(()=>setLoading(false));
  };

  const loadSummary = (f=filters) => {
    const p = new URLSearchParams();
    if (f.from) p.append('from', f.from);
    if (f.to) p.append('to', f.to);
    get('/inventory-events/summary?'+p.toString())
      .then(d=>setSummary(d.data||[]));
  };

  const applyFilters = () => { loadLedger(); loadSummary(); };

  return (
    <div className="page">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.8rem',color:'#2C1F16',margin:'0 0 4px'}}>
            Stock Ledger
          </h2>
          <p style={{color:'#8A8A8A',fontSize:13,margin:0}}>Complete history of all inventory movements</p>
        </div>
        <div
          style={{
            display:'flex',
            alignItems:'center',
            justifyContent:'flex-end',
            flexWrap:'wrap',
            gap:10
          }}
        >

          <div
            style={{
              display:'flex',
              border:'1px solid #ddd',
              borderRadius:6,
              overflow:'hidden'
            }}
          >
            {['ledger','summary'].map(v=>(
              <button key={v} onClick={()=>setView(v)}
                style={{padding:'6px 16px',border:'none',fontSize:11,cursor:'pointer',
                  background:view===v?'#556B2F':'#fff',
                  color:view===v?'#fff':'#555',fontWeight:view===v?600:400,
                  textTransform:'capitalize'}}>
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:16,
        background:'#F9F8F5',padding:12,borderRadius:10}}>
        <input className="form-control" placeholder="Search item..."
          value={filters.search} onChange={e=>setFilters({...filters,search:e.target.value})}
          style={{maxWidth:180,fontSize:12}} />
        <select className="form-control" value={filters.item_type}
          onChange={e=>setFilters({...filters,item_type:e.target.value})}
          style={{maxWidth:140,fontSize:12}}>
          <option value="">All Items</option>
          <option value="material">Materials</option>
          <option value="product">Products</option>
        </select>
        <select className="form-control" value={filters.event_type}
          onChange={e=>setFilters({...filters,event_type:e.target.value})}
          style={{maxWidth:180,fontSize:12}}>
          <option value="">All Events</option>
          {Object.entries(EVENT_COLORS).map(([k,v])=>(
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <input type="date" className="form-control" value={filters.from}
          onChange={e=>setFilters({...filters,from:e.target.value})}
          style={{maxWidth:150,fontSize:12}} />
        <input type="date" className="form-control" value={filters.to}
          onChange={e=>setFilters({...filters,to:e.target.value})}
          style={{maxWidth:150,fontSize:12}} />
        <button className="btn btn-primary btn-sm" onClick={applyFilters}>Apply</button>
        <button className="btn btn-ghost btn-sm" onClick={()=>{
          setFilters({item_type:'',event_type:'',from:'',to:'',search:''});
          loadLedger({item_type:'',event_type:'',from:'',to:'',search:''});
          loadSummary({from:'',to:''});
        }}>Clear</button>
      </div>

      {/* LEDGER VIEW */}
      {view==='ledger' && (
        loading ? <div className="spinner"/> : events.length === 0 ? (
          <div className="empty-state">
            <h3>No stock movements yet</h3>
            <p>Movements will appear here when materials are issued, received or adjusted</p>
          </div>
        ) : (
          <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'#2C1F16'}}>
                  {['Date','Item','Type','Change','Before','After','Reference','By'].map(h=>(
                    <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:9,
                      color:'#E8C466',textTransform:'uppercase',letterSpacing:'.08em',fontWeight:700}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {events.map((e,i)=>{
                  const ev = EVENT_COLORS[e.event_type]||{bg:'#F9F8F5',color:'#8A8A8A',label:e.event_type,icon:'📌'};
                  const isIn = parseFloat(e.quantity_change) > 0;
                  return (
                    <tr key={e.id} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                        color:'#8A8A8A',whiteSpace:'nowrap'}}>
                        {new Date(e.created_at).toLocaleDateString('en-NG',{
                          day:'numeric',month:'short',year:'numeric'
                        })}
                        <div style={{fontSize:10,color:'#aaa'}}>
                          {new Date(e.created_at).toLocaleTimeString('en-NG',{
                            hour:'2-digit',minute:'2-digit'
                          })}
      </div>
                      </td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                        <div style={{fontWeight:600,color:'#2C1F16'}}>{e.item_name}</div>
                        <div style={{fontSize:10,color:'#8A8A8A',textTransform:'capitalize'}}>{e.item_type}</div>
                      </td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                        <span style={{fontSize:9,padding:'3px 8px',borderRadius:20,fontWeight:600,
                          background:ev.bg,color:ev.color}}>
                          {ev.icon} {ev.label}
                        </span>
                      </td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                        fontWeight:700,color:isIn?'#2E6B3A':'#8F2E24',fontSize:14}}>
                        {isIn?'+':''}{parseFloat(e.quantity_change).toLocaleString('en-NG')}
                      </td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                        color:'#8A8A8A'}}>
                        {parseFloat(e.quantity_before||0).toLocaleString('en-NG')}
                      </td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                        fontWeight:600,color:'#2C1F16'}}>
                        {parseFloat(e.quantity_after||0).toLocaleString('en-NG')}
                      </td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                        fontSize:11,color:'#8A8A8A'}}>
                        {e.reference_number||e.reference_type||'—'}
                        {e.notes && <div style={{fontSize:10,color:'#aaa'}}>{e.notes}</div>}
                      </td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                        fontSize:11,color:'#555'}}>{e.recorded_by_name||'—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* SUMMARY VIEW */}
      {view==='summary' && (
        summary.length === 0 ? (
          <div className="empty-state"><h3>No movements in this period</h3></div>
        ) : (
          <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'#2C1F16'}}>
                  {['Item','Type','Total In','Total Out','Net Change','Events','Last Movement'].map(h=>(
                    <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:9,
                      color:'#E8C466',textTransform:'uppercase',letterSpacing:'.08em',fontWeight:700}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summary.map((s,i)=>(
                  <tr key={s.item_id} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                      fontWeight:600,color:'#2C1F16'}}>{s.item_name}</td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                      color:'#8A8A8A',textTransform:'capitalize'}}>{s.item_type}</td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                      color:'#2E6B3A',fontWeight:600}}>
                      +{parseFloat(s.total_in||0).toLocaleString('en-NG')}
                    </td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                      color:'#8F2E24',fontWeight:600}}>
                      -{parseFloat(s.total_out||0).toLocaleString('en-NG')}
                    </td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                      fontWeight:700,color:parseFloat(s.net_change)>=0?'#2E6B3A':'#8F2E24'}}>
                      {parseFloat(s.net_change)>=0?'+':''}{parseFloat(s.net_change||0).toLocaleString('en-NG')}
                    </td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                      textAlign:'center'}}>{s.event_count}</td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                      color:'#8A8A8A',fontSize:11}}>
                      {new Date(s.last_movement).toLocaleDateString('en-NG',{
                        day:'numeric',month:'short',year:'numeric'
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
